<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTheme } from '@/composables/useTheme';

const { t } = useI18n({ useScope: 'global' });

const ACCENT_KEY = 'nxt-admin-accent';
const DEFAULT_ACCENT = '#111111';

const { isDark, setTheme } = useTheme();

// Couleur principale sélectionnée
const accentColors = computed(() => [
  { value: '#111111', label: t('theme.colorBlack') },
  { value: '#006398', label: t('theme.colorBlue') },
  { value: '#2e7d32', label: t('theme.colorGreen') },
  { value: '#7c3aed', label: t('theme.colorPurple') },
  { value: '#be123c', label: t('theme.colorRed') },
]);
const selectedAccent = ref(DEFAULT_ACCENT);

// Appliquer la couleur sur les variables CSS globales
function applyAccent(color: string) {
  const root = document.documentElement;
  root.style.setProperty('--nxt-primary', color);
  root.style.setProperty('--nxt-primary-container', color);
  root.style.setProperty('--nxt-primary-medium', color + 'cc');
}

function saveAccent() {
  applyAccent(selectedAccent.value);
  localStorage.setItem(ACCENT_KEY, selectedAccent.value);
  saved.value = true;
  setTimeout(() => {
    saved.value = false;
  }, 2000);
}

function resetAccent() {
  selectedAccent.value = DEFAULT_ACCENT;
  applyAccent(DEFAULT_ACCENT);
  localStorage.removeItem(ACCENT_KEY);
}

// Charger la couleur sauvegardée au montage
onMounted(() => {
  const stored = localStorage.getItem(ACCENT_KEY);
  if (stored) {
    selectedAccent.value = stored;
  }
});

const saved = ref(false);

// Typographie
const fonts = computed(() => [
  t('theme.fontDefault'),
  'Plus Jakarta Sans',
  'Hanken Grotesk',
]);
const selectedFont = ref('');
const fontSize = ref(14);

// Arrondi
const radiusOptions = computed(() => [
  { key: 'sharp', label: t('theme.radiusSharp') },
  { key: 'soft', label: t('theme.radiusSoft') },
  { key: 'round', label: t('theme.radiusRound') },
]);
const selectedRadius = ref('soft');

// Effets
const glassEffect = ref(true);
const darkForced = ref(false);

// Preview card background selon glass effect
const previewCardStyle = computed(() =>
  glassEffect.value
    ? 'background:rgba(255,255,255,0.4);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,0.25)'
    : 'background:#ffffff;border:1px solid rgba(0,0,0,0.08)',
);
</script>

<template>
  <div class="tsp">
    <!-- ── Panneau gauche : réglages ───────────────────────── -->
    <div class="tsp__panel">
      <!-- Thème clair / sombre -->
      <section class="tsp__section">
        <div class="tsp__section-head">
          <i class="pi pi-palette tsp__section-icon" />
          <h3 class="tsp__section-title">{{ t('theme.appearance') }}</h3>
        </div>
        <div class="tsp__theme-row">
          <button
            class="tsp__theme-btn"
            :class="{ 'tsp__theme-btn--active': !isDark }"
            @click="setTheme('light')"
          >
            <i class="pi pi-sun" />
            <span>{{ t('theme.light') }}</span>
            <i v-if="!isDark" class="pi pi-check tsp__theme-check" />
          </button>
          <button
            class="tsp__theme-btn"
            :class="{ 'tsp__theme-btn--active': isDark }"
            @click="setTheme('dark')"
          >
            <i class="pi pi-moon" />
            <span>{{ t('theme.dark') }}</span>
            <i v-if="isDark" class="pi pi-check tsp__theme-check" />
          </button>
        </div>
      </section>

      <!-- Couleurs -->
      <section class="tsp__section">
        <div class="tsp__section-head">
          <i class="pi pi-palette tsp__section-icon" />
          <h3 class="tsp__section-title">{{ t('theme.brandColors') }}</h3>
        </div>
        <div class="tsp__field">
          <label class="tsp__label">{{ t('theme.primaryColor') }}</label>
          <div class="tsp__swatches">
            <button
              v-for="c in accentColors"
              :key="c.value"
              class="tsp__swatch"
              :class="{ 'tsp__swatch--active': selectedAccent === c.value }"
              :style="{ background: c.value }"
              :title="c.label"
              @click="selectedAccent = c.value"
            />
            <label
              class="tsp__swatch tsp__swatch--add"
              :title="t('theme.customColor')"
            >
              <i class="pi pi-plus" style="font-size: 0.75rem; color: #555" />
              <input
                type="color"
                class="tsp__color-input"
                :value="selectedAccent"
                @input="
                  (e: Event) =>
                    (selectedAccent = (e.target as HTMLInputElement).value)
                "
              />
            </label>
          </div>
        </div>
        <div class="tsp__two-col">
          <div class="tsp__field">
            <label class="tsp__label">{{ t('theme.background') }}</label>
            <input type="color" class="tsp__color-picker" value="#f8f9fa" />
          </div>
          <div class="tsp__field">
            <label class="tsp__label">{{ t('theme.surfaces') }}</label>
            <input type="color" class="tsp__color-picker" value="#ffffff" />
          </div>
        </div>
      </section>

      <!-- Typographie -->
      <section class="tsp__section">
        <div class="tsp__section-head">
          <i class="pi pi-align-left tsp__section-icon" />
          <h3 class="tsp__section-title">{{ t('theme.typography') }}</h3>
        </div>
        <div class="tsp__field">
          <label class="tsp__label">{{ t('theme.fontFamily') }}</label>
          <select v-model="selectedFont" class="tsp__select">
            <option v-for="f in fonts" :key="f">{{ f }}</option>
          </select>
        </div>
        <div class="tsp__field">
          <label class="tsp__label tsp__label--row">
            {{ t('theme.fontSize') }}
            <span class="tsp__label-val">{{ fontSize }}px</span>
          </label>
          <input
            v-model.number="fontSize"
            type="range"
            min="12"
            max="18"
            class="tsp__range"
          />
        </div>
      </section>

      <!-- Formes & Effets -->
      <section class="tsp__section">
        <div class="tsp__section-head">
          <i class="pi pi-th-large tsp__section-icon" />
          <h3 class="tsp__section-title">{{ t('theme.shapesEffects') }}</h3>
        </div>
        <div class="tsp__field">
          <label class="tsp__label">{{ t('theme.cornerRadius') }}</label>
          <div class="tsp__radius-row">
            <button
              v-for="r in radiusOptions"
              :key="r.key"
              class="tsp__radius-btn"
              :class="{ 'tsp__radius-btn--active': selectedRadius === r.key }"
              @click="selectedRadius = r.key"
            >
              {{ r.label }}
            </button>
          </div>
        </div>
        <div class="tsp__toggle-row">
          <div>
            <p class="tsp__toggle-label">{{ t('theme.glassEffect') }}</p>
            <p class="tsp__toggle-sub">{{ t('theme.glassEffectSub') }}</p>
          </div>
          <label class="tsp__toggle">
            <input
              v-model="glassEffect"
              type="checkbox"
              class="tsp__toggle-input"
            />
            <span class="tsp__toggle-track" />
          </label>
        </div>
        <div class="tsp__toggle-row">
          <div>
            <p class="tsp__toggle-label">{{ t('theme.forcedDark') }}</p>
            <p class="tsp__toggle-sub">{{ t('theme.forcedDarkSub') }}</p>
          </div>
          <label class="tsp__toggle">
            <input
              v-model="darkForced"
              type="checkbox"
              class="tsp__toggle-input"
            />
            <span class="tsp__toggle-track" />
          </label>
        </div>
      </section>
    </div>

    <!-- ── Preview droite ──────────────────────────────────── -->
    <div class="tsp__preview-col">
      <div class="tsp__preview-label">
        <span>{{ t('theme.livePreview') }}</span>
        <div class="tsp__preview-modes">
          <button class="tsp__preview-mode tsp__preview-mode--active">
            <i class="pi pi-desktop" />
          </button>
          <button class="tsp__preview-mode"><i class="pi pi-tablet" /></button>
          <button class="tsp__preview-mode"><i class="pi pi-mobile" /></button>
        </div>
      </div>

      <div class="tsp__canvas">
        <!-- Halos déco -->
        <div
          class="tsp__halo tsp__halo--tr"
          :style="{ background: selectedAccent + '22' }"
        />
        <div
          class="tsp__halo tsp__halo--bl"
          :style="{ background: selectedAccent + '11' }"
        />

        <!-- Carte preview -->
        <div class="tsp__card" :style="previewCardStyle">
          <div class="tsp__card-head">
            <div>
              <h4 class="tsp__card-title" :style="{ color: selectedAccent }">
                Nextep Pay
              </h4>
              <p class="tsp__card-sub">Encaissez partout où vous vendez.</p>
            </div>
            <div class="tsp__card-icon" :style="{ background: selectedAccent }">
              <i
                class="pi pi-credit-card"
                style="color: #fff; font-size: 1.125rem"
              />
            </div>
          </div>

          <div class="tsp__card-kpis">
            <div class="tsp__kpi">
              <p class="tsp__kpi-label">TOTAL AUJOURD'HUI</p>
              <p class="tsp__kpi-value">1 248,50 €</p>
              <span class="tsp__kpi-chip tsp__kpi-chip--green"
                >+12% vs hier</span
              >
            </div>
            <div class="tsp__kpi">
              <p class="tsp__kpi-label">TAUX DE REFUS</p>
              <p class="tsp__kpi-value">0,8 %</p>
              <span
                class="tsp__kpi-chip"
                :style="{
                  background: selectedAccent + '18',
                  color: selectedAccent,
                }"
                >Stable</span
              >
            </div>
          </div>

          <div class="tsp__card-rows">
            <div class="tsp__row">
              <div class="tsp__row-left">
                <div class="tsp__row-icon" style="background: #111">
                  <i
                    class="pi pi-desktop"
                    style="color: #fff; font-size: 0.75rem"
                  />
                </div>
                <span>Terminal — Comptoir 1</span>
              </div>
              <span class="tsp__row-amount">24,50 €</span>
            </div>
            <div class="tsp__row">
              <div class="tsp__row-left">
                <div
                  class="tsp__row-icon"
                  :style="{ background: selectedAccent }"
                >
                  <i
                    class="pi pi-qrcode"
                    style="color: #fff; font-size: 0.75rem"
                  />
                </div>
                <span>QR code — Table 6</span>
              </div>
              <span class="tsp__row-amount">61,00 €</span>
            </div>
          </div>

          <div class="tsp__card-actions">
            <button
              class="tsp__action-btn tsp__action-btn--primary"
              :style="{ background: selectedAccent }"
            >
              Rejoindre la liste
            </button>
            <button
              class="tsp__action-btn tsp__action-btn--outline"
              :style="{ borderColor: selectedAccent, color: selectedAccent }"
            >
              Consulter les tarifs
            </button>
          </div>

          <!-- Notif flottante -->
          <div class="tsp__notif">
            <div class="tsp__notif-icon">
              <i
                class="pi pi-check-circle"
                style="color: #16a34a; font-size: 1rem"
              />
            </div>
            <div>
              <p class="tsp__notif-title">Paiement confirmé</p>
              <p class="tsp__notif-sub">QR — Table 6 • 61,00 €</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="tsp__actions">
        <button class="tsp__btn tsp__btn--ghost" @click="resetAccent">
          <i class="pi pi-refresh" />
          {{ t('theme.reset') }}
        </button>
        <button
          class="tsp__btn tsp__btn--primary"
          :class="{ 'tsp__btn--saved': saved }"
          @click="saveAccent"
        >
          <i :class="saved ? 'pi pi-check' : 'pi pi-save'" />
          {{ saved ? t('theme.saved') : t('theme.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Layout principal ── */
.tsp {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .tsp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .tsp {
    padding: 1rem;
    gap: 1rem;
  }
}
@media (min-width: 1024px) {
  .tsp {
    grid-template-columns: 340px 1fr;
    gap: 1.75rem;
  }
}

/* ── Panneau gauche ── */
.tsp__panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tsp__section {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.25rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tsp__section-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.125rem;
}
.tsp__section-icon {
  font-size: 0.9rem;
  color: var(--nxt-on-surface-variant);
}
.tsp__section-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

/* Thème clair/sombre */
.tsp__theme-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.625rem;
}
.tsp__theme-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: var(--nxt-surface-low, #f8f9fa);
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}
.tsp__theme-btn--active {
  border-color: #141c29;
  background: rgba(20, 28, 41, 0.05);
  color: #141c29;
}
.tsp__theme-check {
  margin-left: auto;
  font-size: 0.75rem;
  color: #141c29;
}

/* Champs */
.tsp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.tsp__label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
}
.tsp__label--row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.tsp__label-val {
  font-weight: 700;
  color: var(--nxt-on-surface);
  text-transform: none;
  letter-spacing: 0;
}

/* Swatches */
.tsp__swatches {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}
.tsp__swatch {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid transparent;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
  outline: none;
}
.tsp__swatch:hover {
  transform: scale(1.12);
}
.tsp__swatch--active {
  box-shadow:
    0 0 0 2px #fff,
    0 0 0 4px #141c29;
}
.tsp__swatch--add {
  background: var(--nxt-surface-high, #e7e8e9);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}
.tsp__color-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  border: none;
  padding: 0;
}

.tsp__two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}
.tsp__color-picker {
  width: 100%;
  height: 2.5rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant);
  cursor: pointer;
  background: none;
  padding: 0.2rem;
}

/* Select */
.tsp__select {
  height: 2.625rem;
  border-radius: 0.75rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: var(--nxt-surface-low, #f8f9fa);
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  padding: 0 0.875rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.15s ease;
  width: 100%;
}
.tsp__select:focus {
  border-color: #141c29;
}

/* Range */
.tsp__range {
  width: 100%;
  accent-color: #141c29;
}

/* Radius */
.tsp__radius-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
}
.tsp__radius-btn {
  padding: 0.5rem 0;
  border-radius: 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  background: var(--nxt-surface-low, #f8f9fa);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}
.tsp__radius-btn--active {
  border-color: #141c29;
  background: rgba(20, 28, 41, 0.05);
  color: #141c29;
}

/* Toggles */
.tsp__toggle-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.07));
  gap: 1rem;
}
.tsp__toggle-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  margin: 0;
}
.tsp__toggle-sub {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  margin: 0.15rem 0 0;
}
.tsp__toggle {
  position: relative;
  flex-shrink: 0;
}
.tsp__toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.tsp__toggle-track {
  display: block;
  width: 2.625rem;
  height: 1.5rem;
  background: var(--nxt-surface-highest, #e1e3e4);
  border-radius: 9999px;
  cursor: pointer;
  transition: background 0.2s ease;
  position: relative;
}
.tsp__toggle-track::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 1.25rem;
  height: 1.25rem;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.tsp__toggle-input:checked ~ .tsp__toggle-track {
  background: var(--nxt-primary, #141c29);
}
.tsp__toggle-input:checked ~ .tsp__toggle-track::after {
  transform: translateX(1.125rem);
}

/* ── Colonne droite preview ── */
.tsp__preview-col {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.tsp__preview-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-on-surface-variant);
}
.tsp__preview-modes {
  display: flex;
  gap: 0.25rem;
}
.tsp__preview-mode {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.15s ease;
}
.tsp__preview-mode:hover {
  background: var(--nxt-surface-high, #e7e8e9);
}
.tsp__preview-mode--active {
  background: var(--nxt-surface-highest, #e1e3e4);
  color: var(--nxt-on-surface);
}

/* Canvas */
.tsp__canvas {
  flex: 1;
  min-height: 480px;
  border-radius: 1.5rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  background-image: radial-gradient(
    circle at 2px 2px,
    rgba(0, 0, 0, 0.04) 1px,
    transparent 0
  );
  background-size: 24px 24px;
  background-color: var(--nxt-surface-low, #f8f9fa);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.tsp__halo {
  position: absolute;
  width: 24rem;
  height: 24rem;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
}
.tsp__halo--tr {
  top: -8rem;
  right: -8rem;
}
.tsp__halo--bl {
  bottom: -8rem;
  left: -8rem;
}

/* Carte preview */
.tsp__card {
  width: 100%;
  max-width: 520px;
  border-radius: 1.5rem;
  padding: 1.75rem;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  transition: background 0.3s ease;
}

.tsp__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.tsp__card-title {
  margin: 0 0 0.25rem;
  font-size: 1.375rem;
  font-weight: 800;
}
.tsp__card-sub {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
}
.tsp__card-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.tsp__card-kpis {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem;
}
.tsp__kpi {
  background: rgba(255, 255, 255, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 0.875rem;
  padding: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.tsp__kpi-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #666;
  margin: 0;
}
.tsp__kpi-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: #111;
  margin: 0;
}
.tsp__kpi-chip {
  display: inline-block;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
}
.tsp__kpi-chip--green {
  background: #dcfce7;
  color: #166534;
}

.tsp__card-rows {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.tsp__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 0.875rem;
  font-weight: 600;
}
.tsp__row-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.tsp__row-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tsp__row-amount {
  font-weight: 700;
}

.tsp__card-actions {
  display: flex;
  gap: 0.75rem;
}
.tsp__action-btn {
  flex: 1;
  height: 2.75rem;
  border-radius: 0.75rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: filter 0.15s ease;
}
.tsp__action-btn:hover {
  filter: brightness(1.08);
}
.tsp__action-btn--primary {
  color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.tsp__action-btn--outline {
  background: transparent;
  border: 2px solid;
}

/* Notif flottante */
.tsp__notif {
  position: absolute;
  bottom: 1.25rem;
  right: 1.25rem;
  background: #fff;
  border-radius: 0.875rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  animation: tsp-bounce 2s infinite;
}
.tsp__notif-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: #dcfce7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tsp__notif-title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #111;
  margin: 0;
}
.tsp__notif-sub {
  font-size: 0.6875rem;
  color: #666;
  margin: 0.1rem 0 0;
}
@keyframes tsp-bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

/* Boutons d'action bas de page */
.tsp__actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}
.tsp__btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.75rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition:
    filter 0.15s ease,
    transform 0.1s ease;
}
.tsp__btn:active {
  transform: scale(0.97);
}
.tsp__btn--ghost {
  background: var(--nxt-surface-low, #f8f9fa);
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  color: var(--nxt-on-surface-variant);
}
.tsp__btn--ghost:hover {
  background: var(--nxt-surface-high, #e7e8e9);
}
.tsp__btn--primary {
  background: var(--nxt-primary, #141c29);
  color: #fff;
  box-shadow: 0 4px 12px rgba(20, 28, 41, 0.25);
}
.tsp__btn--primary:hover {
  filter: brightness(1.15);
}
.tsp__btn--saved {
  background: #16a34a !important;
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3) !important;
}
</style>

<style>
[data-theme='dark'] .tsp__section {
  background: rgba(30, 32, 36, 0.7);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .tsp__canvas {
  background-color: rgba(15, 17, 20, 0.8);
  background-image: radial-gradient(
    circle at 2px 2px,
    rgba(255, 255, 255, 0.04) 1px,
    transparent 0
  );
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .tsp__theme-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .tsp__theme-btn--active {
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}
[data-theme='dark'] .tsp__select {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
}
[data-theme='dark'] .tsp__radius-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .tsp__radius-btn--active {
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}
[data-theme='dark'] .tsp__toggle-track {
  background: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .tsp__btn--ghost {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
}
</style>
